import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { API } from "@/App";
import {
  User, EnvelopeSimple, Lock, Eye, EyeSlash, Spinner, ArrowLeft,
  CheckCircle, ChartLineUp, GraduationCap, Wrench, ShieldCheck,
  Sparkle, ArrowRight,
} from "@phosphor-icons/react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";

  useEffect(() => {
    if (user && typeof user === "object") {
      if (user.experience_level == null) navigate("/onboarding", { replace: true });
      else navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        const loggedIn = await login(email, password);
        if (loggedIn && loggedIn.experience_level == null) navigate("/onboarding", { replace: true });
        else navigate(redirectTo, { replace: true });
      } else if (mode === "register") {
        await register(email, password, name);
        navigate("/onboarding", { replace: true });
      } else if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setSuccess("If an account with that email exists, a password reset link has been sent. Check your inbox.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  const title = mode === "login" ? "Welcome Back" : mode === "register" ? "Join The Nest" : "Reset Password";
  const sub =
    mode === "login" ? "Sign in to access live markets, AI forecasting, and your watchlist."
    : mode === "register" ? "Create your account — it takes 30 seconds."
    : "Enter your email and we'll send you a secure reset link.";

  return (
    <div className="min-h-[calc(100vh-44px)] flex items-center justify-center px-4 py-8 relative" data-testid="auth-page">
      {/* Dark scrim so the form stays readable on the wallpaper */}
      <div className="absolute inset-0 bg-black/65 backdrop-blur-[3px] -z-10" aria-hidden="true" />

      <div className="w-full max-w-md relative">
        {/* Brand header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center mb-3">
            <img
              src="/logo.jpeg"
              alt="The Golden Nest"
              className="w-14 h-14 rounded-full ring-2 ring-amber-400/60 shadow-[0_0_30px_-4px_rgba(252,211,77,0.7)]"
              data-testid="auth-logo"
            />
          </div>
          <h1
            className="font-heading font-black uppercase tracking-[0.18em] nest-title-text"
            style={{ fontSize: "clamp(26px, 4.5vw, 38px)", lineHeight: 1 }}
            data-testid="auth-brand-title"
          >
            The Golden Nest
          </h1>
          <p className="text-[10px] text-amber-400/80 uppercase tracking-[0.3em] mt-2">
            Data · Education · Tools
          </p>
        </div>

        {/* Glass card */}
        <div className="auth-card relative rounded-lg p-6 sm:p-7" data-testid="auth-card">
          <div className="mb-5 text-center">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-zinc-50" data-testid="auth-title">{title}</h2>
            <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{sub}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="auth-form">
            {mode === "forgot" && (
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-amber-300 transition-colors -mt-1 mb-1"
                data-testid="back-to-login"
              >
                <ArrowLeft size={12} /> Back to Sign In
              </button>
            )}

            {mode === "register" && (
              <AuthField
                label="Name"
                icon={<User size={15} />}
                type="text"
                value={name}
                onChange={setName}
                placeholder="Your name"
                testId="auth-name"
              />
            )}

            <AuthField
              label="Email"
              icon={<EnvelopeSimple size={15} />}
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@email.com"
              required
              testId="auth-email"
            />

            {mode !== "forgot" && (
              <AuthField
                label="Password"
                icon={<Lock size={15} />}
                type={showPw ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder={mode === "login" ? "Your password" : "Min 6 characters"}
                required
                minLength={6}
                testId="auth-password"
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="text-zinc-500 hover:text-amber-300 transition-colors p-1"
                    data-testid="auth-toggle-pw"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeSlash size={15} /> : <Eye size={15} />}
                  </button>
                }
              />
            )}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  data-testid="forgot-password-link"
                  className="text-[11px] text-amber-400/80 hover:text-amber-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <p
                className="text-xs text-red-300 bg-red-950/40 border border-red-700/40 rounded px-3 py-2 leading-snug"
                data-testid="auth-error"
              >
                {error}
              </p>
            )}

            {success && (
              <div
                className="text-xs text-emerald-300 bg-emerald-950/40 border border-emerald-700/40 rounded px-3 py-2 flex items-start gap-2"
                data-testid="auth-success"
              >
                <CheckCircle size={14} className="shrink-0 mt-0.5" weight="fill" />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="auth-submit-btn w-full text-sm font-bold tracking-wide py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Spinner size={15} className="animate-spin" />
                  <span>Please wait…</span>
                </>
              ) : (
                <>
                  <span>
                    {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
                  </span>
                  <ArrowRight size={14} weight="bold" />
                </>
              )}
            </button>

            {mode !== "forgot" && (
              <p className="text-center text-[11px] text-zinc-400 pt-1">
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                <button
                  type="button"
                  onClick={() => switchMode(mode === "login" ? "register" : "login")}
                  data-testid="auth-toggle"
                  className="text-amber-300 hover:text-amber-200 transition-colors font-semibold"
                >
                  {mode === "login" ? "Sign Up" : "Sign In"}
                </button>
              </p>
            )}
          </form>
        </div>

        {/* Pillars + trust strip */}
        {mode !== "forgot" && (
          <>
            <div className="grid grid-cols-3 gap-2 mt-5" data-testid="auth-pillars">
              <Pillar icon={<ChartLineUp size={16} weight="bold" />} tint="emerald" label="Data" sub="Live crypto · stocks · macro" />
              <Pillar icon={<GraduationCap size={16} weight="bold" />} tint="blue" label="Education" sub="Academy · Financial Ed" />
              <Pillar icon={<Wrench size={16} weight="bold" />} tint="amber" label="Tools" sub="Goals · Alerts · Forecasts" />
            </div>

            <div className="flex items-center justify-center gap-4 mt-4 text-[10px] text-zinc-500" data-testid="auth-trust">
              <span className="flex items-center gap-1">
                <ShieldCheck size={12} className="text-emerald-400" /> Encrypted
              </span>
              <span className="flex items-center gap-1">
                <Sparkle size={12} className="text-amber-400" weight="fill" /> Ad-free
              </span>
              <span className="text-zinc-600">·</span>
              <span>No card required</span>
            </div>
          </>
        )}

        <p className="text-center text-[10px] text-zinc-600 mt-5 italic">
          Not financial advice. Markets carry risk.
        </p>
      </div>
    </div>
  );
}

/* Reusable themed input field */
function AuthField({ label, icon, type, value, onChange, placeholder, required, minLength, testId, rightSlot }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.18em] text-amber-400/70 block mb-1.5 font-semibold">
        {label}
      </label>
      <div className="relative auth-input-wrap">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400/70 pointer-events-none">
          {icon}
        </span>
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          data-testid={testId}
          className="auth-input w-full pl-10 pr-10 py-3 text-sm rounded-md focus:outline-none"
        />
        {rightSlot && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
    </div>
  );
}

/* Reusable pillar card */
function Pillar({ icon, tint, label, sub }) {
  const tintMap = {
    emerald: "text-emerald-400 border-emerald-500/20",
    blue: "text-blue-400 border-blue-500/20",
    amber: "text-amber-400 border-amber-500/20",
  };
  return (
    <div className={`rounded-md border ${tintMap[tint]} bg-zinc-950/60 backdrop-blur-sm p-2.5 text-center hover:bg-zinc-900/70 transition-colors`}>
      <div className={`${tintMap[tint].split(" ")[0]} flex justify-center mb-1`}>{icon}</div>
      <p className="text-[10px] font-bold text-zinc-100">{label}</p>
      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">{sub}</p>
    </div>
  );
}
